import React, { useEffect, useState } from "react"
import {
  Badge,
  Button,
  Card,
  CardBody,
  Col,
  Container,
  Input,
  Row,
  Spinner,
  Table,
} from "reactstrap"
import { useDispatch, useSelector } from "react-redux"
import { useLocation, useNavigate } from "react-router-dom"
import { useTranslation } from "react-i18next"
import Breadcrumbs from "components/Common/Breadcrumb"
import LocalizedDatePicker from "components/Common/LocalizedDatePicker"
import Paginations from "components/Common/Paginations"
import { saGetAllInvoices, saDownloadInvoice } from "store/actions/superAdminAction"
import { downloadInvoicePdf } from "helpers/invoicePdf"
import { showAlert } from "store/reducers/alertReducer"
import { ROUTES } from "constants/routes"
import { navigateToAccessibleRoute } from "utils/navigateUnauthorized"
import isPermissionEnabled from "common/isPermissionEnabled"
import { translateStatusLabel } from "utils/statusLabels"

const STATUS_COLORS = {
  paid: "success",
  pending: "warning",
  failed: "danger",
  refunded: "info",
}

const AllInvoices = () => {
  const { t } = useTranslation()
  document.title = t("All Invoices | Super Admin")

  const dispatch = useDispatch()
  const location = useLocation()
  const navigate = useNavigate()
  const pageNumber = parseInt(new URLSearchParams(location.search).get("page")) || 1

  const { userRole, accountType, accountScope } = useSelector(state => state.Login)
  const isSuperAdminSession =
    accountScope === "super_admin" || accountType === "superadmin"
  const canViewBilling = isPermissionEnabled(
    userRole,
    106,
    "sa_billing_1",
    accountType,
    accountScope
  )

  const { invoicesLoading, invoices, invoicesTotalCount, invoicesTotalPages } = useSelector(
    state => state.SuperAdmin
  )

  const [statusFilter, setStatusFilter] = useState("")
  const [tenantFilter, setTenantFilter] = useState("")
  const [dateFrom, setDateFrom] = useState("")
  const [dateTo, setDateTo] = useState("")

  useEffect(() => {
    if (!isSuperAdminSession) {
      navigateToAccessibleRoute(navigate)
      return
    }
    if (!canViewBilling) {
      navigateToAccessibleRoute(navigate)
      return
    }
    dispatch(
      saGetAllInvoices({
        page: pageNumber,
        limit: 10,
        status: statusFilter || undefined,
        tenantName: tenantFilter || undefined,
        dateFrom: dateFrom || undefined,
        dateTo: dateTo || undefined,
      })
    )
  }, [
    dispatch,
    pageNumber,
    statusFilter,
    tenantFilter,
    dateFrom,
    dateTo,
    isSuperAdminSession,
    canViewBilling,
    navigate,
  ])

  const handleDownload = invoice => {
    dispatch(saDownloadInvoice({ id: invoice._id })).then(res => {
      const payload = res?.payload || {}
      const data = payload.data || {}
      if (payload.status && data.downloadUrl) {
        window.open(data.downloadUrl, "_blank")
        return
      }
      if (payload.status && data.invoice) {
        try {
          downloadInvoicePdf(data.invoice, {
            issuerName: t("Wulooj"),
            labels: {
              invoice: t("Invoice"),
              date: t("Date"),
              status: t("Status"),
              billTo: t("Billing Info"),
              company: t("Company"),
              email: t("Email"),
              address: t("Address"),
              description: t("Plan"),
              billingCycle: t("Billing Cycle"),
              period: t("Period"),
              amount: t("Amount"),
              total: t("Total Amount"),
              paymentGateway: t("Payment Gateway"),
              transactionId: t("Transaction ID"),
              thankYou: t("Thank you for your business."),
            },
          })
          dispatch(
            showAlert({
              type: "success",
              message: t("Invoice PDF downloaded successfully"),
            })
          )
        } catch {
          dispatch(
            showAlert({ type: "error", message: t("Failed to download invoice") })
          )
        }
        return
      }
      dispatch(
        showAlert({ type: "error", message: t("Failed to download invoice") })
      )
    })
  }

  const handlePageChange = page => {
    const params = new URLSearchParams(location.search)
    params.set("page", page.toString())
    navigate(`${location.pathname}?${params.toString()}`)
  }

  const handleReset = () => {
    setStatusFilter("")
    setTenantFilter("")
    setDateFrom("")
    setDateTo("")
    navigate(`${location.pathname}?page=1`)
    dispatch(saGetAllInvoices({ page: 1, limit: 10 }))
  }

  return (
    <React.Fragment>
      <div className="page-content">
        <Container fluid>
          <Breadcrumbs />
          <h4 className="mb-1">{t("All Invoices")}</h4>
          <p className="text-muted mb-3">{t("Cross-tenant invoice management")}</p>

          <Card>
            <CardBody>
              {/* Filters */}
              <Row className="mb-3 g-2">
                <Col md={3}>
                  <Input
                    type="select"
                    value={statusFilter}
                    onChange={e => setStatusFilter(e.target.value)}
                  >
                    <option value="">{t("All Statuses")}</option>
                    <option value="paid">{t("Paid")}</option>
                    <option value="pending">{t("Pending")}</option>
                    <option value="failed">{t("Failed")}</option>
                    <option value="refunded">{t("Refunded")}</option>
                  </Input>
                </Col>
                <Col md={3}>
                  <Input
                    placeholder={t("Filter by tenant name")}
                    value={tenantFilter}
                    onChange={e => setTenantFilter(e.target.value)}
                  />
                </Col>
                <Col md={3}>
                  <LocalizedDatePicker
                    value={dateFrom}
                    onChange={setDateFrom}
                    placeholder={t("From date")}
                  />
                </Col>
                <Col md={3}>
                  <LocalizedDatePicker
                    value={dateTo}
                    onChange={setDateTo}
                    placeholder={t("To date")}
                  />
                </Col>
                <Col md={12} className="d-flex justify-content-end">
                  <Button
                    color="light"
                    className="d-flex align-items-center"
                    onClick={handleReset}
                    disabled={invoicesLoading}
                  >
                    <i className="mdi mdi-refresh me-1" style={{ fontSize: 18 }} />
                    {t("Reset")}
                  </Button>
                </Col>
              </Row>

              {invoicesLoading ? (
                <div className="text-center py-5">
                  <Spinner color="primary" />
                </div>
              ) : invoices.length === 0 ? (
                <div className="text-center py-5 text-muted">
                  {t("No invoices found.")}
                </div>
              ) : (
                <>
                  <div className="table-responsive">
                    <Table className="table align-middle table-nowrap mb-0">
                      <thead className="table-light">
                        <tr>
                          <th>{t("Invoice #")}</th>
                          <th>{t("Tenant")}</th>
                          <th>{t("Plan")}</th>
                          <th>{t("Date")}</th>
                          <th>{t("Amount")}</th>
                          <th>{t("Status")}</th>
                          <th>{t("Download")}</th>
                        </tr>
                      </thead>
                      <tbody>
                        {invoices.map((inv, idx) => (
                          <tr key={inv._id || idx}>
                            <td>{inv.invoiceNumber || inv._id}</td>
                            <td className="fw-medium">
                              {inv.tenant?.name || inv.tenantName || "—"}
                            </td>
                            <td>{inv.plan?.name || inv.planName || inv.metadata?.planName || "—"}</td>
                            <td>
                              {inv.createdAt
                                ? new Date(inv.createdAt).toLocaleDateString()
                                : "—"}
                            </td>
                            <td className="invoice-amount">
                              {inv.currency || "SAR"}{" "}
                              {Number(inv.amount || 0).toFixed(2)}
                            </td>
                            <td>
                              <Badge
                                color={STATUS_COLORS[inv.status] || "secondary"}
                                className="text-capitalize"
                              >
                                {translateStatusLabel(t, inv.status)}
                              </Badge>
                            </td>
                            <td>
                              <Button
                                color="link"
                                size="sm"
                                className="p-0 text-primary"
                                onClick={() => handleDownload(inv)}
                              >
                                <i className="bx bx-download font-size-16" />
                              </Button>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </Table>
                  </div>

                  <Paginations
                    currentPage={pageNumber}
                    totalPages={invoicesTotalPages}
                    onPageChange={handlePageChange}
                    totalCount={invoicesTotalCount}
                  />
                </>
              )}
            </CardBody>
          </Card>
        </Container>
      </div>
    </React.Fragment>
  )
}

export default AllInvoices
